import React, { useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useMutation, useQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Button, Typography } from '@mui/material';

import { CREATE_PETOLO_LEAD_WITH_CUSTOMER_DATA } from '../../../graphql/mutations/leads';
import {
  PETOLO_PET_HEALTH_POLICY_CATEGORIES,
  GET_CUSTOMER,
  PETOLO_INSURE_ANOTHER_PET_URL,
  imgLinks,
  GETOLO_UTM_PARAMS,
} from '../../../shared';

import {
  StyledInsureAnotherPetBanner,
  StyledInsureAnotherPetBannerCloseButton,
  StyledInsureAnotherPetBannerContainer,
  StyledInsureAnotherPetBannerContent,
  StyledInsureAnotherPetBannerContentDescription,
  StyledInsureAnotherPetBannerContentTitle,
  StyledInsureAnotherPetBannerPetImage,
  StyledInsureAnotherPetBannerPetoloLogo,
} from './InsureAnotherPetBanner.styled';
import {
  SplashImageBottomLeftCorner,
  SplashImageBottomRight,
  SplashImageTopCenter,
  SplashImageTopRightCorner,
  SplashImageTopRightCornerMobile,
} from './SplashImages';

const utmSource = `utm_source=${GETOLO_UTM_PARAMS.source}`;
const utmCampaign = `utm_campaign=${GETOLO_UTM_PARAMS.campaignBannerInsureAnotherPet}`;

const goToFunnel = ({ uuid }) => {
  if (!uuid) return;

  const urlToInsureAnotherPetFunnel = `${PETOLO_INSURE_ANOTHER_PET_URL}&uuid=${uuid}&${utmSource}&${utmCampaign}`;

  window.open(urlToInsureAnotherPetFunnel, '_blank');
};

const handleRequestComplete = (data) => {
  const newUuid = data?.createLeadWithCustomerData?.uuid;

  if (newUuid) {
    goToFunnel({ uuid: newUuid });
  }
};

function InsureAnotherPetBanner() {
  const { data: customerData } = useQuery(GET_CUSTOMER);
  const customer = customerData?.customer;
  const { contract } = customer;

  const [showBanner, setShowBanner] = useState(true);

  const [
    createLeadWithCustomerData,
    { data: response, called: newUuidCalled, loading: newUuidLoading },
  ] = useMutation(CREATE_PETOLO_LEAD_WITH_CUSTOMER_DATA);

  const allowedCategories = [...PETOLO_PET_HEALTH_POLICY_CATEGORIES];

  const handleCloseBanner = (ev) => {
    if (ev) ev.preventDefault();

    setShowBanner(false);
  };

  const handleInsureAnotherPetBtnClick = () => {
    const newLeadUuid = response?.createLeadWithCustomerData?.uuid;

    if (newLeadUuid) {
      goToFunnel({ uuid: newLeadUuid });

      return;
    }

    createLeadWithCustomerData({
      onCompleted: handleRequestComplete,
    });
  };

  const shouldHideBanner = !showBanner || !allowedCategories?.includes(contract?.policyCategory);
  const disableButton = newUuidCalled && newUuidLoading;

  if (shouldHideBanner) return null;

  return (
    <CSSTransition timeout={400} classNames="fade-in">
      <StyledInsureAnotherPetBannerContainer>
        <SplashImageBottomLeftCorner />
        <SplashImageTopCenter />
        <SplashImageTopRightCorner />
        <SplashImageBottomRight />
        <SplashImageTopRightCornerMobile />

        <StyledInsureAnotherPetBannerCloseButton
          icon="icon.close"
          size="xs"
          alt="close"
          onClick={handleCloseBanner}
        />

        <StyledInsureAnotherPetBanner>
          <StyledInsureAnotherPetBannerPetoloLogo
            src={imgLinks['company_logo.petolo']}
            alt="petolo"
          />

          <StyledInsureAnotherPetBannerContent>
            <StyledInsureAnotherPetBannerContentTitle variant="h3">
              <FormattedMessage id="insure_another_pet.banner.title" />
            </StyledInsureAnotherPetBannerContentTitle>

            <StyledInsureAnotherPetBannerContentDescription variant="p">
              <FormattedMessage id="insure_another_pet.banner.description" />
            </StyledInsureAnotherPetBannerContentDescription>

            <Button type="button" onClick={handleInsureAnotherPetBtnClick} disabled={disableButton}>
              <Typography variant="buttons">
                <FormattedMessage id="insure_another_pet.banner.button.label" />
              </Typography>
            </Button>
          </StyledInsureAnotherPetBannerContent>

          <StyledInsureAnotherPetBannerPetImage
            src={imgLinks['image.insure.another.pet.banner']}
            alt="pets"
          />
        </StyledInsureAnotherPetBanner>
      </StyledInsureAnotherPetBannerContainer>
    </CSSTransition>
  );
}

export default InsureAnotherPetBanner;
